import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileCheck2,
  RefreshCw,
  Search,
  ShieldCheck,
  Truck,
  XCircle,
} from "lucide-react";
import { selectCompanyAddress, selectPrefix } from "../../utility/Redux/profile";
import { selectToken } from "../../utility/Redux/security";
import EInvoiceFlowModal from "../saleSummary/EInvoiceFlowModal";

const api = axios.create({
  baseURL: "https://retailxapp.com",
  headers: { "Content-Type": "application/json" },
});

const statusStyle = {
  GENERATED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  CANCELLED: "bg-slate-100 text-slate-600 ring-slate-200",
  FAILED: "bg-red-50 text-red-700 ring-red-200",
  PROCESSING: "bg-amber-50 text-amber-700 ring-amber-200",
};

const statusLabel = {
  GENERATED: "IRN generated",
  CANCELLED: "Cancelled",
  FAILED: "Needs attention",
  PROCESSING: "Processing",
};

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const dateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
};

const remainingTime = (deadline, now) => {
  if (!deadline) return "Window unavailable";
  const remaining = new Date(deadline).getTime() - now;
  if (remaining <= 0) return "24-hour window expired";
  const hours = Math.floor(remaining / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);
  return `${hours}h ${minutes}m left`;
};

function Metric({ icon: Icon, label, value, tone }) {
  const tones = {
    orange: "bg-orange-50 text-orange-600",
    green: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    red: "bg-red-50 text-red-600",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{Number(value || 0)}</p>
        </div>
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon size={21} />
        </span>
      </div>
    </div>
  );
}

export default function EInvoiceHub() {
  const tablePrefix = useSelector(selectPrefix);
  const token = useSelector(selectToken);
  const companyDetails = useSelector(selectCompanyAddress);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const loadInvoices = useCallback(async ({ page = 1, silent = false } = {}) => {
    if (!tablePrefix || !token) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      const response = await api.post(
        "/api/einvoice/hub/list",
        {
          tablePrefix,
          search: search.trim(),
          status,
          page,
          limit: 25,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRecords(response.data?.records || []);
      setSummary(response.data?.summary || {});
      setPagination(response.data?.pagination || { page, total: 0, totalPages: 1 });
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          (requestError.code === "ERR_NETWORK"
            ? "E-invoice service is not reachable right now."
            : "E-invoices could not be loaded."),
      );
    } finally {
      setLoading(false);
    }
  }, [search, status, tablePrefix, token]);

  useEffect(() => {
    const timer = window.setTimeout(() => loadInvoices({ page: 1 }), 300);
    return () => window.clearTimeout(timer);
  }, [loadInvoices]);

  const emptyMessage = useMemo(() => {
    if (search.trim() || status !== "ALL") return "No e-invoices match the selected filters.";
    return "Invoices generated from Sale Summary will appear here.";
  }, [search, status]);

  const statusOptions = [
    ["ALL", "All"],
    ["GENERATED", "Generated"],
    ["PROCESSING", "Processing"],
    ["FAILED", "Failed"],
    ["CANCELLED", "Cancelled"],
  ];

  return (
    <main className="h-full overflow-auto bg-slate-50 p-4 text-slate-900 md:p-6">
      <div className="mx-auto max-w-[1500px]">
        <section className="relative overflow-hidden rounded-2xl bg-slate-950 px-5 py-6 text-white shadow-xl md:px-7">
          <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-orange-500/25 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-bold text-orange-100">
                <ShieldCheck size={15} /> Compliance workspace
              </div>
              <h1 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">E‑Invoice Hub</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                Track IRNs created from Sale Summary, generate e-way bills, and act on eligible cancellations from one place.
              </p>
            </div>
            <button
              type="button"
              onClick={() => loadInvoices({ page: pagination.page, silent: true })}
              disabled={loading}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-bold text-white transition hover:bg-white/15 disabled:opacity-50"
            >
              <RefreshCw size={17} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </section>

        <section className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric icon={FileCheck2} label="Total e-invoices" value={summary.total} tone="orange" />
          <Metric icon={CheckCircle2} label="Active IRNs" value={summary.generated} tone="green" />
          <Metric icon={Truck} label="Active e-way bills" value={summary.activeEwayBills} tone="blue" />
          <Metric icon={AlertCircle} label="Needs attention" value={summary.failed} tone="red" />
        </section>

        <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm md:p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex-1 lg:max-w-md">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search invoice, GSTIN, IRN or e-way bill…"
                className="h-11 w-full rounded-xl border border-slate-300 bg-slate-50 pl-11 pr-4 text-sm font-medium outline-none focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {statusOptions.map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => setStatus(value)}
                  className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-bold transition ${
                    status === value ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {error && (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800">
            <XCircle className="mt-0.5 shrink-0" size={19} />
            <div className="flex-1">
              <p className="font-bold">Could not load the hub</p>
              <p className="mt-0.5 text-sm">{error}</p>
            </div>
            <button type="button" onClick={() => loadInvoices({ page: 1 })} className="text-sm font-bold underline">
              Retry
            </button>
          </div>
        )}

        <section className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3 md:px-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-bold text-slate-900">Generated invoice register</h2>
                <p className="text-xs text-slate-500">{pagination.total || 0} record(s)</p>
              </div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                <Clock3 size={14} /> Cancellation windows update automatically
              </div>
            </div>
          </div>

          {loading && !records.length ? (
            <div className="space-y-3 p-5">
              {[1, 2, 3].map((item) => <div key={item} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}
            </div>
          ) : records.length ? (
            <div className="divide-y divide-slate-100">
              {records.map((record) => {
                const irnWindow = record.cancellation?.irn;
                const ewayWindow = record.cancellation?.ewayBill;
                return (
                  <article key={record.id} className="p-4 transition hover:bg-slate-50/70 md:p-5">
                    <div className="grid gap-4 xl:grid-cols-[1.2fr_1.3fr_1fr_auto] xl:items-center">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-bold text-slate-950">Invoice #{record.invoiceNo}</h3>
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ring-1 ${statusStyle[record.status] || statusStyle.PROCESSING}`}>
                            {statusLabel[record.status] || record.status}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-sm font-semibold text-slate-600">{record.buyerName || "Buyer name unavailable"}</p>
                        <p className="mt-1 text-xs text-slate-400">
                          {record.documentDate || record.invoiceDate || "—"} · {record.buyerGstin || "GSTIN unavailable"}
                        </p>
                      </div>

                      <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2.5">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">IRN</p>
                        <p className="mt-1 truncate font-mono text-xs font-semibold text-slate-700" title={record.irn || ""}>
                          {record.irn || record.errorMessage || "Awaiting provider response"}
                        </p>
                        {record.status === "GENERATED" && (
                          <p className={`mt-1.5 text-xs font-bold ${irnWindow?.allowed ? "text-emerald-600" : "text-slate-400"}`}>
                            {remainingTime(irnWindow?.deadline, now)}
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="text-sm font-bold text-slate-900">{money(record.invoiceTotal)}</p>
                        {record.ewayBillNo ? (
                          <>
                            <p className="mt-1 text-xs font-bold text-blue-700">E-way #{record.ewayBillNo}</p>
                            <p className={`mt-1 text-xs font-semibold ${ewayWindow?.allowed ? "text-blue-600" : "text-slate-400"}`}>
                              {remainingTime(ewayWindow?.deadline, now)}
                            </p>
                          </>
                        ) : (
                          <p className="mt-1 text-xs text-slate-400">
                            {record.status === "GENERATED" ? "E-way bill not generated" : dateTime(record.updatedAt)}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => setSelectedInvoice({ INVNO: record.invoiceNo })}
                        className="inline-flex h-10 items-center justify-center rounded-xl bg-orange px-4 text-sm font-bold text-white shadow-sm transition hover:bg-[#e86600] focus:outline-none focus:ring-4 focus:ring-orange-100"
                      >
                        {record.status === "GENERATED" && !record.ewayBillNo ? "Create e-way / Manage" : "View / Manage"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-16 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <FileCheck2 size={22} />
              </span>
              <h3 className="mt-4 font-bold text-slate-800">No e-invoices found</h3>
              <p className="mt-1 text-sm text-slate-500">{emptyMessage}</p>
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
              <p className="text-xs font-semibold text-slate-500">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={pagination.page <= 1 || loading}
                  onClick={() => loadInvoices({ page: pagination.page - 1 })}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={pagination.page >= pagination.totalPages || loading}
                  onClick={() => loadInvoices({ page: pagination.page + 1 })}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <EInvoiceFlowModal
        open={Boolean(selectedInvoice)}
        onClose={() => {
          setSelectedInvoice(null);
          loadInvoices({ page: pagination.page, silent: true });
        }}
        invoice={selectedInvoice}
        tablePrefix={tablePrefix}
        companyDetails={companyDetails}
        token={token}
      />
    </main>
  );
}
